import { ackCommand, uploadScreenshot } from "../api/deviceClient";
import type { DeviceCommand } from "../api/types";
import { getBridge } from "../bridge";

// Execute a remote command via the bridge, then acknowledge it. `refresh` is
// handled by the caller (it must trigger a fresh sync), so it's a no-op here
// beyond the ack.

export async function handleCommands(
  commands: DeviceCommand[],
  onRefresh: () => void,
): Promise<void> {
  const bridge = getBridge();
  for (const cmd of commands) {
    try {
      switch (cmd.type) {
        case "reboot":
          await ackCommand({ command_id: cmd.command_id, status: "done" });
          await bridge.reboot();
          break;
        case "clear_cache":
          await bridge.clearCache();
          await ackCommand({ command_id: cmd.command_id, status: "done" });
          break;
        case "screenshot": {
          // Capture pixels (native shells only; web returns null) → upload the
          // image, then ack with the persisted screenshot id.
          const blob = await bridge.screenshot();
          if (!blob) {
            await ackCommand({
              command_id: cmd.command_id,
              status: "failed",
              detail: "screenshot unsupported on this platform",
            });
            break;
          }
          const shot = await uploadScreenshot(cmd.command_id, blob);
          await ackCommand({
            command_id: cmd.command_id,
            status: "done",
            detail: shot.id,
          });
          break;
        }
        case "refresh":
          onRefresh();
          await ackCommand({ command_id: cmd.command_id, status: "done" });
          break;
        case "update_app":
          // App update is the native shell's job; ack as unsupported on web.
          await ackCommand({
            command_id: cmd.command_id,
            status: "failed",
            detail: "update_app handled by native shell",
          });
          break;
        default:
          await ackCommand({
            command_id: cmd.command_id,
            status: "failed",
            detail: `unknown command type: ${(cmd as DeviceCommand).type}`,
          });
      }
    } catch (err) {
      console.warn(`[commands] ${cmd.type} failed`, err);
      try {
        await ackCommand({
          command_id: cmd.command_id,
          status: "failed",
          detail: String(err),
        });
      } catch {
        /* ack will be retried implicitly when the command reappears in sync */
      }
    }
  }
}
